import React from "react";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import TopImage from "@/components/TopImage";
import AsideImage from "@/components/AsideImage";
import BodyText from "@/components/BodyText";
import RichText from "@/components/RichText";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

export default function About() {
  const t = useContent("about");
  useSeo("about", {
    title: "About Dan Hoover — Quantum Results",
    description:
      "Three decades helping people navigate change, make better decisions, and move forward with confidence.",
  });
  return (
    <div data-testid="page-about">
      <TopImage src={IMG.about.hero.src} alt={IMG.about.hero.alt} credit={IMG.about.hero.credit} testId="about-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "About Dan Hoover")}
        title={t("hero_title", <>I don't believe most people need more <span className="text-brand-blue">information</span>.</>)}
        lede={t("hero_lede", "I believe they need the space to think clearly enough to recognize what they already know.")}
        testId="about-hero"
      />

      <Section testId="about-belief">
        <BodyText
          className=""
          value={t.body(
            "belief_body",
            `Throughout my career, I've discovered that meaningful change rarely begins with having the perfect answer.

It begins with seeing ourselves, our circumstances, and our possibilities more clearly.

That's why everything I create through Quantum Results is designed to help people slow down, think deeply, and move forward with greater intention.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section2_eyebrow", "The question that shaped my work")}
          title={t("section2_title", "One question has guided much of my life's work.")}
          testId="about-question"
        >
          <BodyText
            className=""
            value={t.body(
              "section2_body",
              `*Why do some people continue growing through life's challenges while others remain stuck, even when they have the ability to create something better?*

That question has followed me through executive leadership, technology, coaching, personal development, and countless conversations with people navigating change.

Over time, I began to notice something.

Meaningful change rarely began with a new strategy. It usually began with greater clarity. As people saw themselves and their circumstances more clearly, they made better decisions. And better decisions created different results.

**That observation continues to shape everything I do today.**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section3_eyebrow", "Experience shaped my perspective")}
        title={t("section3_title", "Experience shaped my perspective.")}
        testId="about-experience"
      >
        <BodyText
          className=""
          value={t.body(
            "section3_body",
            `Throughout my career, I've had the opportunity to work in executive leadership, lead complex technology initiatives, coach individuals and teams, and study the principles of personal growth and human potential.

Each experience offered a different perspective.

But together, they revealed a consistent pattern.

**Regardless of the circumstances, meaningful progress almost always began when people saw themselves and their situation more clearly. That realization continues to shape the work I do today.**`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <div className="container-narrow py-20 sm:py-24 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-center" data-testid="about-why">
          <AsideImage
            src={IMG.about.danPortrait.src}
            alt={IMG.about.danPortrait.alt}
            aspect="aspect-[3/4]"
            testId="about-dan-portrait"
          />
          <div className="max-w-xl">
            <RichText as="div" className="eyebrow mb-5" value={t("why_eyebrow", "Why I continue this work")} />
            <RichText
              as="h2"
              className="display text-[30px] sm:text-[40px] lg:text-[46px] mb-8"
              value={t("why_title", "Why I continue this work.")}
            />
            <BodyText
              value={t.body(
                "why_body",
                `I'm still fascinated by the potential people carry within them.

Not because I believe life is easy.

But because I've seen what becomes possible when people begin seeing themselves differently.

Helping someone gain greater clarity, make an important decision, or recognize a possibility they couldn't see before never becomes routine.

Those moments continue to remind me why this work matters.

**It's a privilege I never take for granted.**`
              )}
            />
            <RichText
              as="div"
              className="text-[14px] italic text-brand-ink/55 mt-4"
              value={t("why_signature", "— Dan Hoover")}
            />
          </div>
        </div>
      </section>

      <NaturalNextSteps
        note={t("closing_note", "When you're ready, there are a few thoughtful places to continue.")}
        items={[
          { to: "/four-levels", label: "The Four Levels Experience" },
          { to: "/forward-reset", label: "Forward Reset — a private conversation" },
          { to: "/resources", label: "Resources — read, watch, reflect" },
        ]}
      />
    </div>
  );
}
